import { enumStatus } from "./options";

const MS_PER_MINUTE = 60 * 1000;

// Proporcion final del tiempo limite en la que el contador se marca como "por vencer"
const SOON_RATIO = 0.2;

// Estatus que nunca muestran contador regresivo
const COUNTDOWN_EXCLUDED_STATUSES = [
  enumStatus.INITIATED,
  enumStatus.FINISHED,
  enumStatus.FAILED,
  enumStatus.CANCELED,
  enumStatus.PENDING_PAYMENT,
  // Tienen reglas propias (fecha agendada / frecuencia de seguimiento)
  enumStatus.SCHEDULED,
  enumStatus.FOLLOW_UP,
];

export const normalizeStatusKey = (status) =>
  String(status || "").trim().toLowerCase();

/**
 * Calcula los plazos (en ms epoch) del estatus actual del caso.
 * Usa la misma referencia que `checkCardTime`: `lastStage.createdAt`.
 *
 * Devuelve `null` cuando el caso no debe mostrar contador:
 * - estatus excluidos o con regla propia (estimatedTime, agendado)
 * - estatus sin tiempo limite fijo configurado (vacio, 0 o Maps)
 *
 * @param {object} _case
 * @param {Record<string, {limitTime:number, criticTime:number}>} timeParamsByStatus
 *   Mapa ya filtrado a tiempos fijos (ver `buildTimeParamsByStatus` en Cards.jsx)
 */
export const getStatusDeadlines = (_case, timeParamsByStatus = {}) => {
  const status = _case?.status;
  if (!status) return null;
  if (COUNTDOWN_EXCLUDED_STATUSES.includes(status)) return null;

  // Mismas excepciones que usa checkCardTime con reglas propias
  if (status === enumStatus.CUSTOMER_ON_ROAD && _case?.estimatedTime) {
    return null;
  }
  if (status === enumStatus.TOW_ASSIGNED && _case?.scheduleDate) {
    return null;
  }

  const config = timeParamsByStatus?.[normalizeStatusKey(status)];
  const limitTime = Number(config?.limitTime);
  if (!Number.isFinite(limitTime) || limitTime <= 0) return null;

  const stageStart = _case?.lastStage?.createdAt;
  if (!stageStart) return null;
  const startAt = new Date(stageStart).getTime();
  if (!Number.isFinite(startAt)) return null;

  const criticTime = Number(config?.criticTime);
  const hasCritic = Number.isFinite(criticTime) && criticTime >= limitTime;

  return {
    startAt,
    limitAt: startAt + limitTime * MS_PER_MINUTE,
    criticAt: hasCritic ? startAt + criticTime * MS_PER_MINUTE : null,
    limitMs: limitTime * MS_PER_MINUTE,
  };
};

/**
 * Estado del contador en el instante `nowMs`.
 * phase: "ok" (en tiempo) | "limit" (excedio el limite) | "critic" (excedio el critico)
 */
export const getStatusCountdown = (
  _case,
  timeParamsByStatus = {},
  nowMs = Date.now()
) => {
  const deadlines = getStatusDeadlines(_case, timeParamsByStatus);
  if (!deadlines) return null;

  if (nowMs < deadlines.limitAt) {
    const remainingMs = deadlines.limitAt - nowMs;
    return {
      phase: "ok",
      remainingMs,
      overdueMs: 0,
      isSoon: remainingMs <= deadlines.limitMs * SOON_RATIO,
    };
  }

  const isCritic = deadlines.criticAt != null && nowMs >= deadlines.criticAt;
  return {
    phase: isCritic ? "critic" : "limit",
    remainingMs: 0,
    overdueMs: nowMs - deadlines.limitAt,
    isSoon: false,
  };
};

/**
 * Proximo instante (ms epoch) en el que alguno de los casos cambia de fase,
 * o `null` si no hay ninguno pendiente. Sirve para programar un unico timeout
 * en lugar de re-renderizar periodicamente.
 */
export const getNextPhaseChangeAt = (
  cases,
  timeParamsByStatus = {},
  nowMs = Date.now()
) => {
  let next = null;
  for (let i = 0; i < (cases?.length || 0); i++) {
    const deadlines = getStatusDeadlines(cases[i], timeParamsByStatus);
    if (!deadlines) continue;
    const candidates = [deadlines.limitAt, deadlines.criticAt];
    for (let j = 0; j < candidates.length; j++) {
      const at = candidates[j];
      if (at != null && at > nowMs && (next === null || at < next)) {
        next = at;
      }
    }
  }
  return next;
};

const pad = (n) => String(n).padStart(2, "0");

/** Formatea milisegundos como mm:ss, o h:mm:ss si supera la hora. */
export const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
};
